<template>
  <zm-context :system-fonts="false" :namespace="NAME_SPACE">
    <ZmButton
      role="button"
      type="tertiary"
      :icon="Menu"
      :aria-label="$t('aria.toggleSidebar')"
      :aria-controls="sidebarControlId"
      :aria-expanded="String(isSidebarExpanded)"
      @click="handleToggle"
      @keydown.enter.prevent.stop="handleToggle"
      @keydown.space.prevent.stop="handleToggle"
      @keydown.down.prevent.stop="handleArrowDown" />
  </zm-context>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Menu } from '@zoom/icons-vue'
import { announce, ZmButton, ZmContext } from '@zoom/zoom-ui-vue3'
import { NAME_SPACE } from '@/commons/constants'
import { trackEvent } from '../../../../../shared/event-tracker'

const { t } = useI18n()

const SIDEBAR_ID = 'nep-mobile-sidebar-nav'
const sidebarControlId = ref(SIDEBAR_ID)
const isSidebarExpanded = ref(true)

const getSideMenu = () => document.querySelector('.mini-layout-body > .row > .nav-menu')

const ensureSidebarId = (sideMenu) => {
  if (sideMenu.id) {
    sidebarControlId.value = sideMenu.id
    return
  }

  sideMenu.id = SIDEBAR_ID
  sidebarControlId.value = SIDEBAR_ID
}

const updateSidebarState = () => {
  const sideMenu = getSideMenu()
  if (!sideMenu) return
  sideMenu.style.display = 'none'
  ensureSidebarId(sideMenu)
  const display = window.getComputedStyle(sideMenu).display
  isSidebarExpanded.value = display !== 'none'
}

const handleToggle = () => {
  const nextExpanded = toggleSideMenu()
  const action = nextExpanded ? 'expand' : 'close'
  announce(nextExpanded ? t('sidebarExpanded') : t('sidebarCollapsed'))
  trackEvent({
    linkText: action,
    linkUrl: 'undefined',
    uiLocation: 'universal nav mobile'
  })
}
const toggleSideMenu = () => {
  const sideMenu = getSideMenu()
  if (!sideMenu) return null
  const display = window.getComputedStyle(sideMenu).display
  const nextExpanded = display === 'none'

  ensureSidebarId(sideMenu)
  sideMenu.style.display = nextExpanded ? 'block' : 'none'
  isSidebarExpanded.value = nextExpanded
  return nextExpanded
}

const getFirstFocusableInSidebar = (sideMenu) =>
  sideMenu?.querySelector(
    [
      '[role="treeitem"][tabindex="0"]',
      'a[href]:not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ')
  )

const handleArrowDown = () => {
  const sideMenu = getSideMenu()
  if (!sideMenu || !isSidebarExpanded.value) return
  const firstFocusable = getFirstFocusableInSidebar(sideMenu)
  firstFocusable?.focus()
}

onMounted(() => {
  updateSidebarState()
})

onBeforeUnmount(() => {
  const sideMenu = getSideMenu()
  if (!sideMenu) return

  sideMenu.style.display = 'block'
})

defineExpose({
  toggleSideMenu
})
</script>
